import {isFeatureEnabled} from 'feature-flags';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import {useGetTenantV2Query} from '@dms/api/base';
import {CountryAlpha3CodeEnum} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {i18n} from '@dms/i18n';

import {CurrencyCodeType} from 'shared/web';

import {DEFAULT_COUNTRY} from '../constants/country';
import {DEFAULT_CURRENCY} from '../constants/currency';

/**
 * A hook that provides the tenant data and utility functions related to the tenant.
 * @returns An object containing the tenant data, tenant country, and a function to check if a given country code matches the tenant's country.
 */
export function useTenant() {
  const {data: tenant, isLoading} = useGetTenantV2Query();
  const tenantCountry = (tenant?.country ?? DEFAULT_COUNTRY) as CountryAlpha3CodeEnum;
  const tenantCurrency = (tenant?.currency ?? DEFAULT_CURRENCY) as CurrencyCodeType;
  const handleTenantPhoneInfo = (tenantCountry: CountryAlpha3CodeEnum) => {
    if (tenant?.contactInformation?.selfEmployedInformation?.phoneNumber) {
      return tenant.contactInformation.selfEmployedInformation.phoneNumber;
    } else {
      return match(tenantCountry)
        .with('POL', always({number: '', prefix: '+48', countryCode: tenantCountry}))
        .with('SVK', always({number: '', prefix: '+421', countryCode: tenantCountry}))
        .with('DEU', always({number: '', prefix: '+49', countryCode: tenantCountry}))
        .with('NLD', always({number: '', prefix: '+31', countryCode: tenantCountry}))
        .with('BEL', always({number: '', prefix: '+32', countryCode: tenantCountry}))
        .with('ITA', always({number: '', prefix: '+39', countryCode: tenantCountry}))
        .with('FRA', always({number: '', prefix: '+33', countryCode: tenantCountry}))
        .otherwise(always({number: '', prefix: '+420', countryCode: 'CZE'}));
    }
  };

  const tenantPhoneInfo = handleTenantPhoneInfo(tenantCountry);

  const isTenantCountrySupported = (country: CountryAlpha3CodeEnum) => tenantCountry === country;
  const isTenantCountryRestricted = (country: CountryAlpha3CodeEnum[]) =>
    country.includes(tenantCountry);

  const isMultiLocale =
    isFeatureEnabled(featureFlags.CORE_MULTI_LANGUAGE_SUPPORT) &&
    (tenant?.availableLocales?.length ?? 0) > 1;

  const supportedLocales =
    tenant?.availableLocales?.map((locale) => {
      const [language] = locale.split('-');

      return {
        value: locale,
        label: `${i18n.t(`entity.tenant.labels.language-${language}`)} (${locale})`,
      };
    }) ?? [];

  return {
    /**
     * The tenant data.
     */
    tenant,
    /**
     * The country code of the tenant.
     */
    tenantCountry,
    /**
     * The currency of the tenant.
     */
    tenantCurrency,
    /**
     * The phone number, phone number prefix and the country code of the tenant.
     */
    tenantPhoneInfo,
    /**
     * A function that checks if a given country code matches the tenant's country.
     * @param country - The country code to check.
     * @returns A boolean indicating whether the given country code matches the tenant's country.
     */
    isTenantCountrySupported,
    /**
     * A function that checks if a given country code does matches the tenant's country.
     * @param country - The country code to check.
     * @returns A boolean indicating whether the given country code does not match the tenant's country.
     */
    isTenantCountryRestricted,
    /**
     * Whether the tenant has more than one available locale.
     */
    isMultiLocale,
    /**
     * The supported locales of the tenant.
     */
    supportedLocales,
    /**
     * Whether the tenant data is loading.
     * @returns A boolean indicating whether the tenant data is loading.
     */
    isLoading,
  };
}
